<template>
  <section class="user-info-section" :aria-busy="loadState === 'loading'">
    <div class="header">
      <h2 class="title">{{ $t("myPage.userInfo.title") }}</h2>
      <button type="button" class="update-link" :disabled="loadState !== 'loaded'" @click="onClickUpdate">
        {{ $t("myPage.userInfo.update") }}
      </button>
    </div>

    <my-page-user-info-edit-dialog v-if="showEditDialog" :profile="profile" :nola-penname="nolaPenname"
      @close="showEditDialog = false" @updated="onUpdated" />

    <p v-if="loadState === 'error'" class="load-error" role="alert">{{ $t("myPage.userInfo.loadError") }}</p>

    <div v-else class="fields">
      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.name") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value" aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(displayName) }">{{ displayOrUnset(displayName) }}
        </p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.nameKana") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value" aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(displayNameKana) }">{{
          displayOrUnset(displayNameKana) }}
        </p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.penname") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value" aria-hidden="true"></span>
        <template v-else>
          <ul v-if="pennames.length" class="penname-list">
            <li v-for="(penname, index) in pennames" :key="`${penname}-${index}`" class="penname-item">
              <icon-star v-if="index === 0" class="penname-star" />
              <span v-else class="penname-bullet" aria-hidden="true"></span>
              <span>{{ penname }}</span>
            </li>
          </ul>
          <p v-else class="field-value is-unset">{{ $t("common.unset") }}</p>
        </template>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.phone") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value" aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(displayPhone) }">{{ displayOrUnset(displayPhone) }}
        </p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.gender") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value skeleton-field-value-short"
          aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(displayGender) }">{{ displayOrUnset(displayGender)
        }}</p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.birthdate") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value" aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(displayBirthdate) }">{{
          displayOrUnset(displayBirthdate)
        }}
        </p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.emailAddressLabel") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value skeleton-field-value-wide"
          aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(email) }">{{ displayOrUnset(email) }}</p>
      </div>

      <div class="field">
        <p class="field-label">{{ $t("myPage.userInfo.userId") }}</p>
        <span v-if="loadState === 'loading'" class="skeleton-text skeleton-field-value skeleton-field-value-wide"
          aria-hidden="true"></span>
        <p v-else class="field-value" :class="{ 'is-unset': isUnset(userId) }">{{ displayOrUnset(userId) }}</p>
        <p class="field-note">{{ $t("myPage.userIdNote") }}</p>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import Vue from "vue";
import { Auth } from "aws-amplify";
import { NolaPfContestClient } from "@/lib/clients";
import { User } from "@/lib/models";
import { Gender } from "@/lib/models/nolaContest";
import { NolaPfContestProfile } from "@/lib/models/nolaPfContest";
import { buildDisplayedPennames } from "@/lib/utils/pennameList";
import MyPageUserInfoEditDialog from "@/components/ui/MyPageUserInfoEditDialog.vue";
import IconStar from "~icons/figma/Star";

const nolaPfContestClient = new NolaPfContestClient();

const GENDER_KEYS: Record<Gender, string> = {
  [Gender.male]: "male",
  [Gender.female]: "female",
  [Gender.other]: "other",
};

function formatPhone(phone?: string): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

type ProfileLoadState = "loading" | "loaded" | "error";

function parseBirthdate(birthdate?: string): { year: number; month: number; day: number } | null {
  if (!birthdate) return null;
  const matched = birthdate.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!matched) return null;
  return {
    year: Number(matched[1]),
    month: Number(matched[2]),
    day: Number(matched[3]),
  };
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    IconStar,
    MyPageUserInfoEditDialog,
  },
  data() {
    return {
      profile: null as NolaPfContestProfile | null,
      loadState: "loading" as ProfileLoadState,
      email: "",
      userId: "",
      showEditDialog: false,
    };
  },
  computed: {
    nolaPenname() {
      const user = this.$store.getters["userModule/user"] as User | undefined;
      return user?.name ?? "";
    },
    pennames() {
      return buildDisplayedPennames(this.nolaPenname, this.profile?.pennames);
    },
    displayName() {
      return this.profile?.name ?? "";
    },
    displayNameKana() {
      return this.profile?.nameKana ?? "";
    },
    displayPhone() {
      return formatPhone(this.profile?.phone);
    },
    displayGender() {
      if (!this.profile) return "";
      const gender = Number(this.profile.gender) as Gender;
      const key = GENDER_KEYS[gender];
      if (!key) return "";
      return this.$t(`common.gender.${key}`) as string;
    },
    displayBirthdate() {
      const parsed = parseBirthdate(this.profile?.birthDate);
      if (!parsed) return this.profile?.birthDate ?? "";
      return this.$t("myPage.userInfo.birthdateFormat", parsed) as string;
    },
  },
  async created() {
    const profilePromise = nolaPfContestClient.fetchProfile();
    const userPromise = this.$store.dispatch("userModule/initialize");
    const cognitoPromise = Auth.currentUserPoolUser().catch(() => null);

    try {
      const [profile] = await Promise.all([profilePromise, userPromise]);
      const user = this.$store.getters["userModule/user"] as User | undefined;
      if (!user?.userId) {
        throw new Error("Failed to fetch user");
      }
      this.profile = profile;
      this.loadState = "loaded";
    } catch (error) {
      console.error("Failed to fetch user info:", error);
      this.loadState = "error";
    }

    const cognitoUser = await cognitoPromise;
    if (cognitoUser) {
      this.email = cognitoUser.attributes?.email || cognitoUser.username || "";
      this.userId = cognitoUser.signInUserSession.idToken.payload.sub;
    }
  },
  methods: {
    isUnset(value?: string | null): boolean {
      return !value;
    },
    displayOrUnset(value?: string | null): string {
      return value || (this.$t("common.unset") as string);
    },
    onClickUpdate() {
      if (this.loadState !== "loaded") return;
      this.showEditDialog = true;
    },
    onUpdated(profile: NolaPfContestProfile) {
      this.profile = profile;
    },
  },
});

interface Data {
  profile: NolaPfContestProfile | null;
  loadState: ProfileLoadState;
  email: string;
  userId: string;
  showEditDialog: boolean;
}

interface Computed {
  nolaPenname: string;
  pennames: string[];
  displayName: string;
  displayNameKana: string;
  displayPhone: string;
  displayGender: string;
  displayBirthdate: string;
}

interface Methods {
  isUnset(value?: string | null): boolean;
  displayOrUnset(value?: string | null): string;
  onClickUpdate(): void;
  onUpdated(profile: NolaPfContestProfile): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.user-info-section {
  margin-top: 32px;
}

.header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-text-normal);
}

.update-link {
  position: relative;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-primary);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-xs);
  }

  &:disabled {
    color: var(--color-inactive);
    cursor: not-allowed;

    &::after {
      display: none;
    }
  }
}

.load-error {
  margin: 16px 0 0;
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-warning);
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

.field-label {
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-low);
}

.field-value {
  margin: 4px 0 0;
  font-size: var(--font-size-md);
  line-height: 1.5;
  color: var(--color-text-normal);
  word-break: break-word;

  &.is-unset {
    color: var(--color-text-low);
  }
}

.field-note {
  margin: 4px 0 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-low);
}

.penname-list {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.penname-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-md);
  line-height: 1.5;
  color: var(--color-text-normal);

  &+& {
    margin-top: 4px;
  }
}

.penname-star {
  flex-shrink: 0;
  font-size: 16px;
  color: var(--color-caution);
}

.penname-bullet {
  flex-shrink: 0;
  width: 4px;
  height: 4px;
  margin: 0 6px;
  border-radius: var(--radius-circle);
  background-color: var(--color-text-normal);
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.skeleton-text {
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-xs);
  position: relative;
  overflow: hidden;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: shimmer 1.5s infinite;
  }

  &.skeleton-field-value {
    width: 160px;
    height: 24px;
    margin-top: 4px;
    vertical-align: middle;
  }

  &.skeleton-field-value-short {
    width: 60px;
  }

  &.skeleton-field-value-wide {
    width: 240px;
  }
}
</style>
